import { useEffect } from 'react';
import { useLoadStatus } from 'components/LoadStatus';
import { urlFor } from '@root/lib/sanity';
import { type SanityImageSource } from '@sanity/asset-utils';
import tw from 'tailwind/tailwind-theme-gentux';

export type HeroImageSourceKey = 'base' | 'sm-up' | 'md-up';

export type HeroImageSource = {
  key: HeroImageSourceKey;
  url: string;

  /**
   * The URL pointing to the low resolution version of this image that users will
   * see before the actual image loads
   *
   * If not provided, Imgix's `blur` and `q` params will be added to the `url` to
   * request a blurred version of the source image
   *
   * @see https://docs.imgix.com/en-US/apis/rendering/stylize/gaussian-blur
   * @see https://docs.imgix.com/en-US/apis/rendering/format/output-quality
   */
  placeholderUrl?: string;

  /**
   * A media query for the `<source>` element that specifies when it should be
   * displayed
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/source#media
   * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/picture
   */
  mediaQuery?: string;
};

type HeroImageProps = {
  alt: string;
  sources: HeroImageSource[];
};

/**
 * @todo This can be removed once we've migrated the homepage back into Sanity, since
 *       it's specific to Imgix's rendering API
 */
const getPlaceholderUrl = (url: string) => {
  const placeholderUrl = new URL(url);

  placeholderUrl.searchParams.set('q', '25');
  placeholderUrl.searchParams.set('blur', '100');

  return placeholderUrl.toString();
};

export const getSourcesForImage = (image: SanityImageSource): HeroImageSource[] => {
  const baseImage = urlFor(image).auto('format').fit('clip');

  return [
    {
      key: 'md-up',
      mediaQuery: `(min-width: ${tw.screens.md})`,
      url: baseImage.quality(50).url(),
      placeholderUrl: baseImage.quality(25).blur(100).url(),
    },
    {
      key: 'sm-up',
      mediaQuery: `(min-width: ${tw.screens.sm})`,
      url: baseImage.width(1024).url(),
      placeholderUrl: baseImage.width(1024).quality(25).blur(100).url(),
    },
    {
      key: 'base',
      url: baseImage.width(800).url(),
      placeholderUrl: baseImage.width(800).quality(25).blur(100).url(),
    },
  ];
};

const DIMENSIONS_BY_KEY = {
  base: {
    width: 800,
    height: 571,
  },
  'sm-up': {
    width: 1024,
    height: 731,
  },
  'md-up': {
    width: 2000,
    height: 1428,
  },
} as const;

/**
 * A component that can be used for pictures that are in the critical rendering
 * path, that helps to prevent layout shifting by using a placeholder image and
 * swapping it with the actual, high resolution image once it's loaded
 */
export const HeroImage = ({ alt, sources }: HeroImageProps) => {
  const { hasLoaded, setHasLoaded } = useLoadStatus();

  /**
   * Preload the images from the source set in the background
   */
  useEffect(() => {
    for (let source of sources) {
      if (!hasLoaded(source.url)) {
        const highResImage = new Image();

        highResImage.addEventListener('load', () => setHasLoaded(source.url));

        highResImage.src = source.url;
      }
    }
  }, []);

  return (
    <picture className="inline h-auto w-auto" style={{ aspectRatio: '1.4 / 1' }}>
      {sources.map((source) => (
        <source
          key={source.key}
          media={source.mediaQuery}
          height={DIMENSIONS_BY_KEY[source.key]['height']}
          width={DIMENSIONS_BY_KEY[source.key]['width']}
          srcSet={hasLoaded(source.url) ? source.url : (source.placeholderUrl ?? getPlaceholderUrl(source.url))}
        />
      ))}

      <img className="block h-full w-full object-cover" alt={alt} />
    </picture>
  );
};
